import z from 'zod'
import { CompanyIdSchema, idSchemaV2, TeamIdSchema, customIdSchema, isId } from '../ids'
import { GLOBALTEMPLATE_ID } from '../company'
import { AuditPropsSchema } from '../common'
import { CloserStatusSchema } from '../closer'
import { ArchivableSchema } from '../archive'
import { RedactOptionSchema } from './redact'
import { PITCH_EVAL_QUESTIONS } from './questions'

export const EvalCategoryIdSchema = customIdSchema('C', ':')
export type EvalCategoryId = z.infer<typeof EvalCategoryIdSchema>
export const isEvalCategoryId = isId(EvalCategoryIdSchema)

export const EvalQuestionIdSchema = customIdSchema('Q', ':')
export type EvalQuestionId = z.infer<typeof EvalQuestionIdSchema>
export const isEvalQuestionId = isId(EvalQuestionIdSchema)

export const EvalQuestionSchema = z.object({
  code: EvalQuestionIdSchema,
  title: z.string(),
  criteria: z.string().optional(),
  value: z.number(),
})
export type EvalQuestion = z.infer<typeof EvalQuestionSchema>

export const EvalCategorySchema = z.object({
  code: EvalCategoryIdSchema,
  title: z.string(),
  questions: z.array(EvalQuestionSchema),
})
export type EvalCategory = z.infer<typeof EvalCategorySchema>

export const RubricIdSchema = idSchemaV2('Rubric')
export type RubricId = z.infer<typeof RubricIdSchema>
export const isRubricId = isId(RubricIdSchema)

const FeedbackSchema = z.object({
  score: z.number().describe('The score given for the criteria'),
  feedback: z.string().optional().describe('The feedback provided for the criteria'),
})

export const RubricCriteriaSchema = z.object({
  ...EvalQuestionSchema.shape,
  ai: FeedbackSchema.optional().describe('The AI graded score for the criteria'),
  user: FeedbackSchema.optional().describe('The user feedback for the criteria'),
})
export type RubricCriteria = z.infer<typeof RubricCriteriaSchema>

export const RubricCategorySchema = z.object({
  ...EvalCategorySchema.omit({ questions: true }).shape,
  questions: z.array(RubricCriteriaSchema).describe('The questions for the category'),
  skillShare: z.boolean().optional().describe('Is this category eligible for skill share?'),
  autoShareThreshold: z.number().gte(0).lte(1).optional().describe('The threshold score for automatic skill share'),
})
export type RubricCategory = z.infer<typeof RubricCategorySchema>

export const UnsavedRubricSchema = z.object({
  title: z.string().describe('The title of the rubric'),
  scopeId: z.union([CompanyIdSchema, TeamIdSchema]).optional(),
  isDefault: z.boolean().optional().meta({ deprecated: true }),
  roles: z.array(CloserStatusSchema).optional().describe('The account roles this rubric applies to'),
  description: z.string().optional().describe('The description of the rubric'),
  categories: z.array(RubricCategorySchema).describe('The categories in the rubric'),
  keywords: z.string().array().optional().describe('Positive key words to be marked in the transcript'),
  redact: RedactOptionSchema.array().optional().describe('Options for redacting sensitive information'),
  autoSkillShare: z.boolean().optional().describe('Is this rubric eligible for automatic skill share?'),
})
export type UnsavedRubric = z.infer<typeof UnsavedRubricSchema>

export const RubricDataSchema = z.object({
  ...ArchivableSchema.shape,
  ...AuditPropsSchema.shape,
  ...UnsavedRubricSchema.shape,
  rubricId: RubricIdSchema,
})
export type RubricData = z.infer<typeof RubricDataSchema>

export type RubricScore = { total: number; points: number }

export const DEFAULT_RUBRIC_AUTO_SHARE_THRESHOLD = 0.75

export const DEFAULT_PITCH_RUBRIC: RubricData = {
  rubricId: 'Rubric.DEFAULT',
  title: 'Default Rubric',
  scopeId: GLOBALTEMPLATE_ID,
  categories: PITCH_EVAL_QUESTIONS,
  redact: ['credit_card', 'credit_card_expiration', 'cvv', 'ssn'],
}
